// src/pages/crm/FunnelsGallery.jsx
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { HiLockClosed, HiUsers, HiGlobeAlt, HiPlus } from "react-icons/hi2";
import axios from "../../config/axiosInstance";
import { CRM, TEAMS } from "../../config/apiConfig";
import { useApplication } from "../../contexts/ApplicationContext";
import FunnelTemplateModal from "../../components/modals/FunnelTemplateModal";
import FunnelCustomBuilderModal from "../../components/modals/FunnelCustomBuilderModal";
import { FaPlus } from "react-icons/fa6";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";

/** =================== helpers =================== */
const toArray = (maybe) => {
  if (Array.isArray(maybe)) return maybe;
  if (maybe?.rows && Array.isArray(maybe.rows)) return maybe.rows;
  if (maybe?.items && Array.isArray(maybe.items)) return maybe.items;
  if (maybe?.data && Array.isArray(maybe.data)) return maybe.data;
  return [];
};

// Paleta sólida (sem gradiente)
// Paleta sólida (refinada)
const PALETTES = [
  // —— Brand / Warm
  { bg: "#E35412", fg: "#FFFFFF", subtle: "#B23F0E" }, // Laranja brand
  { bg: "#F97316", fg: "#FFFFFF", subtle: "#D65F12" }, // Laranja vivo
  { bg: "#FB923C", fg: "#111827", subtle: "#E07D2F" }, // Laranja suave

  // —— Amber / Gold
  { bg: "#F59E0B", fg: "#111827", subtle: "#D98A09" },
  { bg: "#D97706", fg: "#111827", subtle: "#B96505" },

  // —— Teal / Cyan
  { bg: "#0E7490", fg: "#FFFFFF", subtle: "#095A70" },
  { bg: "#0891B2", fg: "#FFFFFF", subtle: "#077494" },
  { bg: "#06B6D4", fg: "#0B1320", subtle: "#0793AC" },

  // —— Blue
  { bg: "#1D4ED8", fg: "#FFFFFF", subtle: "#1538A3" },
  { bg: "#3B82F6", fg: "#FFFFFF", subtle: "#2E6CD3" },

  // —— Violet
  { bg: "#7C3AED", fg: "#FFFFFF", subtle: "#602CC0" },
  { bg: "#8B5CF6", fg: "#0B1320", subtle: "#6D47C8" },

  // —— Neutral / Slate
  { bg: "#1F2937", fg: "#FFFFFF", subtle: "#111827" },
  { bg: "#0B1320", fg: "#FFFFFF", subtle: "#080F1A" },
];

const hash = (s = "") =>
  Array.from(s).reduce(
    (acc, ch) => ((acc << 5) - acc + ch.charCodeAt(0)) | 0,
    0
  );
const pickSolid = (seed) =>
  PALETTES[Math.abs(hash(seed || "")) % PALETTES.length];

/** pega nome do time robustamente (suporta vários formatos) */
function resolveTeamName(f) {
  return (
    f?.team?.name ??
    f?.teamName ??
    f?.team ??
    f?.groupName ??
    f?.squadName ??
    "Sem time"
  );
}

function ChooseVisibilityModal({
  show,
  onClose,
  onPickPrivate,
  onPickPublic,
  teams = [],
  loadingTeams = false,
  onPickTeam, // (teamId, teamName)
}) {
  const [selectedTeam, setSelectedTeam] = React.useState(
    teams?.[0]?.teamId || null
  );

  React.useEffect(() => {
    if (show) {
      setSelectedTeam(teams?.[0]?.teamId || null);
    }
  }, [show, teams]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[80]">
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"
        onClick={onClose}
      />
      {/* modal */}
      <div className="absolute inset-0 grid place-items-center p-3">
        <div className="w-full max-w-lg rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#0b0f1a] shadow-xl">
          <div className="px-5 pt-4 pb-3 border-b border-gray-100 dark:border-white/10">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Novo quadro — escolha a visibilidade
            </h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Você pode criar um quadro privado, do time (selecione abaixo) ou
              público.
            </p>
          </div>

          <div className="p-5 space-y-4">
            {/* Privado */}
            <button
              type="button"
              onClick={onPickPrivate}
              className="w-full flex items-center justify-between rounded-xl border border-gray-200 dark:border-white/10 px-4 py-3 hover:border-[#E35412] hover:bg-[#E35412]/5 transition"
            >
              <div className="flex items-center gap-3">
                <span className="h-9 w-9 rounded-lg grid place-items-center bg-gray-100 dark:bg-white/10">
                  <HiLockClosed className="h-5 w-5" />
                </span>
                <div className="text-left">
                  <div className="text-sm font-semibold">Privado</div>
                  <div className="text-xs text-gray-500">
                    Visível apenas para você
                  </div>
                </div>
              </div>
              <span className="text-xs text-gray-500">Selecionar</span>
            </button>

            {/* Time */}
            <div className="rounded-xl border border-gray-200 dark:border-white/10 p-4">
              <div className="flex items-center gap-3">
                <span className="h-9 w-9 rounded-lg grid place-items-center bg-gray-100 dark:bg-white/10">
                  <HiUsers className="h-5 w-5" />
                </span>
                <div className="flex-1">
                  <div className="text-sm font-semibold">Time</div>
                  <div className="text-xs text-gray-500">
                    Compartilhe com pessoas do mesmo time
                  </div>
                </div>
              </div>

              {teams?.length > 0 ? (
                <div className="mt-3 flex items-center gap-3">
                  <select
                    value={selectedTeam ?? ""}
                    onChange={(e) => setSelectedTeam(e.target.value || null)}
                    className="w-full rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-transparent px-3 py-2 text-sm"
                  >
                    {teams.map((t) => (
                      <option
                        key={t.teamId ?? t.teamName}
                        value={t.teamId ?? ""}
                      >
                        {t.teamName}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      const t =
                        teams.find(
                          (x) =>
                            String(x.teamId ?? "") ===
                            String(selectedTeam ?? "")
                        ) || teams[0];
                      onPickTeam?.(t?.teamId ?? null, t?.teamName ?? "");
                    }}
                    className="shrink-0 rounded-lg bg-[#E35412] text-white text-sm px-3 py-2 font-semibold hover:brightness-110"
                  >
                    Usar time
                  </button>
                </div>
              ) : loadingTeams ? (
                <div className="mt-3 text-xs text-gray-500">
                  Carregando times...
                </div>
              ) : (
                <div className="mt-3 text-xs text-gray-500">
                  Você ainda não faz parte de nenhum time. Crie como público ou
                  privado.
                </div>
              )}
            </div>

            {/* Público */}
            <button
              type="button"
              onClick={onPickPublic}
              className="w-full flex items-center justify-between rounded-xl border border-gray-200 dark:border-white/10 px-4 py-3 hover:border-[#E35412] hover:bg-[#E35412]/5 transition"
            >
              <div className="flex items-center gap-3">
                <span className="h-9 w-9 rounded-lg grid place-items-center bg-gray-100 dark:bg-white/10">
                  <HiGlobeAlt className="h-5 w-5" />
                </span>
                <div className="text-left">
                  <div className="text-sm font-semibold">Público</div>
                  <div className="text-xs text-gray-500">
                    Visível para toda a organização
                  </div>
                </div>
              </div>
              <span className="text-xs text-gray-500">Selecionar</span>
            </button>
          </div>

          <div className="px-5 py-3 border-t border-gray-100 dark:border-white/10 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="text-sm rounded-lg px-3 py-2 border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ====== Header ====== */
function GalleryHeader({
  title = "Quadros do FVR",
  subtitle = "Organize seus funis por time, público e privado",
  count,
  onCreate,
  onSearch,
}) {
  return (
    <header className="relative overflow-hidden rounded-2xl border border-gray-200/80 dark:border-white/10 bg-white dark:bg-[#0b0f1a] shadow-[0_8px_30px_rgba(0,0,0,.06)]">
      {/* Fundo com gradiente forte */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(227,84,18,0.10) 0%, rgba(227,84,18,0.04) 32%, transparent 60%), radial-gradient(800px 380px at 105% -10%, rgba(227,84,18,0.18), transparent 45%), radial-gradient(700px 340px at -10% 120%, rgba(255,153,51,0.16), transparent 48%)",
        }}
        aria-hidden
      />
      {/* Textura em grid mais evidente */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.09]"
        style={{
          backgroundImage:
            "linear-gradient(0deg, rgba(0,0,0,.12) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,.12) 1px, transparent 1px)",
          backgroundSize: "16px 16px",
        }}
        aria-hidden
      />

      {/* Barra superior animada */}
      <div
        className="absolute top-0 left-0 right-0 h-[6px] overflow-hidden"
        aria-hidden
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, #E35412 0%, #F59E0B 50%, #E35412 100%)",
          }}
        />
        <div
          className="absolute -left-1/3 top-0 h-full w-1/3 animate-[sheen_2.8s_ease-in-out_infinite]"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(255,255,255,.9), transparent)",
            filter: "blur(4px)",
          }}
        />
      </div>

      {/* Vinheta diagonal no canto */}
      <div
        className="pointer-events-none absolute -top-10 -right-10 h-40 w-40 rotate-45 rounded-3xl opacity-20"
        style={{
          background:
            "radial-gradient(closest-side, rgba(255,255,255,.35), rgba(255,255,255,0))",
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,.25)",
        }}
        aria-hidden
      />

      {/* Conteúdo */}
      <div className="relative px-5 py-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Esquerda: título + selo */}
          <div className="flex items-start sm:items-center gap-3">
            <span
              className="mt-0.5 inline-block h-8 w-8 shrink-0 rounded-xl ring-1 ring-black/10"
              style={{
                background:
                  "conic-gradient(from 180deg at 50% 50%, #E35412, #F59E0B, #E35412)",
              }}
            />
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-[22px] sm:text-[26px] font-extrabold tracking-tight text-slate-900 dark:text-white">
                  {title}
                </h1>
                {typeof count === "number" && (
                  <span className="inline-flex items-center gap-1 text-[11px] sm:text-xs px-2.5 py-1 rounded-full border border-gray-300/70 dark:border-white/15 text-gray-800 dark:text-gray-100 bg-white/70 dark:bg-white/10 backdrop-blur-md shadow-[0_1px_0_rgba(0,0,0,.05)]">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#E35412]" />
                    {count} quadro{count === 1 ? "" : "s"}
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                {subtitle}
              </p>
            </div>
          </div>

          {/* Direita: busca + ação */}
          <div className="flex items-center gap-2 w-full md:w-auto">
            {/* IMPORTANTE: classe 'group' para o sheen aparecer */}
            <button
              type="button"
              onClick={onCreate}
              className="group relative inline-flex items-center gap-2 rounded-xl border border-transparent bg-[#E35412] px-3.5 py-2.5 text-sm font-semibold text-white shadow-md shadow-[#E35412]/20 hover:brightness-[1.08] active:scale-[.99] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#E35412]/50 hidden md:flex"
            >
              {/* brilho em diagonal */}
              <span className="pointer-events-none absolute inset-0 overflow-hidden rounded-xl">
                <span
                  className="absolute -left-1/3 top-0 h-full w-1/3 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent, rgba(255,255,255,.9), transparent)",
                    filter: "blur(8px)",
                  }}
                />
              </span>
              <span className="inline-block h-2 w-2 rounded-full bg-white/90" />
              Novo Quadro
            </button>

            {/* FAB mobile (bem destacado) */}
            <button
              type="button"
              onClick={onCreate}
              className="md:hidden w-12 h-12 flex items-center justify-center fixed bottom-5 right-5 bg-[#E35412] rounded-full text-white shadow-xl shadow-[#E35412]/30 z-10"
              aria-label="Novo Quadro"
            >
              <svg
                viewBox="0 0 24 24"
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Keyframes locais */}
      <style>{`
        @keyframes sheen {
          0%   { transform: translateX(-120%); }
          55%  { transform: translateX(130%); }
          100% { transform: translateX(130%); }
        }
      `}</style>
    </header>
  );
}

/** =================== API =================== */
async function getFunnels() {
  const res = await axios.get(CRM.FUNNELS);
  return toArray(res?.data?.data ?? res?.data);
}
async function getTeams() {
  const res = await axios.get(TEAMS.MY_TEAMS);
  return toArray(res?.data?.data ?? res?.data);
}

/** =================== Cards =================== */
function FunnelCard({ funnel, onClick }) {
  const seed = String(funnel.id || funnel.name || "F");
  const { bg, fg, subtle } = pickSolid(seed);
  const v = String(funnel.visibility || "PRIVATE").toUpperCase();
  const Icon =
    v === "PUBLIC" ? HiGlobeAlt : v === "TEAM" ? HiUsers : HiLockClosed;

  return (
    <button
      onClick={onClick}
      className="group relative h-24 w-full rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-black/10"
      style={{ transform: "translateY(var(--lift,0))" }}
      onMouseEnter={(e) => e.currentTarget.style.setProperty("--lift", "-2px")}
      onMouseLeave={(e) => e.currentTarget.style.setProperty("--lift", "0px")}
    >
      <div className="absolute inset-0" style={{ backgroundColor: bg }} />
      <div
        className="absolute top-0 left-0 right-0 h-2"
        style={{ backgroundColor: subtle }}
      />
      <div className="absolute inset-0 rounded-2xl ring-1 ring-black/10" />
      <div className="absolute inset-[2px] rounded-[18px] ring-1 ring-white/10" />
      <div className="relative h-full px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="h-11 w-11 rounded-xl grid place-items-center shadow-inner ring-1"
            style={{
              backgroundColor: subtle,
              color: fg,
              boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)",
              ringColor: "rgba(0,0,0,0.08)",
            }}
          >
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0 text-left">
            <h3
              className="text-base font-semibold truncate"
              style={{ color: fg }}
              title={funnel.name}
            >
              {funnel.name}
            </h3>
            <p className="text-[11px] opacity-80" style={{ color: fg }}>
              {v === "PUBLIC"
                ? "Público"
                : v === "TEAM"
                ? "Do time"
                : "Privado"}
            </p>
          </div>
        </div>
        <span
          className="absolute bottom-2 right-2 items-center gap-1 text-[12px] font-medium px-2 py-1 rounded-full border transition-transform group-hover:translate-x-0.5"
          style={{
            color: fg,
            borderColor: "rgba(255,255,255,0.25)",
            backgroundColor: "rgba(255,255,255,0.06)",
          }}
        >
          Abrir →
        </span>
      </div>
    </button>
  );
}

function AddFunnelCard({ onClick, label = "Novo Quadro" }) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      className="group relative h-24 w-full rounded-2xl border-2 border-dashed
             border-gray-300 dark:border-white/15 bg-gray-100 dark:bg-white/5
             hover:border-[#E35412] dark:hover:border-white/25
             hover:bg-gray-100/60 dark:hover:bg-white/10
             transition-all duration-200
             shadow-sm hover:shadow
             focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500/40"
    >
      <div className="pointer-events-none absolute inset-[6px] rounded-xl border border-gray-200/70 dark:border-white/10" />
      <div className="relative grid h-full place-items-center">
        <div className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
          <span
            className="h-9 w-9 rounded-full border border-gray-300 grid place-items-center
                   transition-all duration-200
                   group-hover:bg-[#E35412] group-hover:text-white
                   dark:group-hover:bg-white dark:group-hover:text-black shadow-sm"
          >
            <HiPlus className="h-4 w-4" />
          </span>
          <div className="text-left">
            <div className="text-sm font-semibold leading-none">{label}</div>
            <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
              Criar e personalizar etapas
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

/** Seção flexível (suporta botão de "adicionar" específico) */
function Section({
  title,
  subtitle,
  countLabel,
  items = [],
  onOpen,
  onAdd,
  addLabel = "Novo Quadro",
  accent = "#111827",
}) {
  const hasItems = items.length > 0;

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-3 px-1">
        <span
          className="inline-block h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: accent }}
        />
        <div>
          <h2 className="text-lg font-semibold tracking-wide text-gray-900 dark:text-white">
            {title}
          </h2>
          {subtitle ? (
            <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
          ) : null}
        </div>
        <span className="ml-1 text-[11px] px-2 py-0.5 rounded-full border border-gray-300/70 dark:border-white/15 text-gray-600 dark:text-gray-300">
          {countLabel ?? `${items.length} quadro${items.length > 1 ? "s" : ""}`}
        </span>
      </div>

      <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {hasItems &&
          items.map((f) => (
            <FunnelCard key={f.id} funnel={f} onClick={() => onOpen(f)} />
          ))}
        {/* Botão "Novo" sempre visível na grade da seção */}
        <AddFunnelCard onClick={onAdd} label={addLabel} />
      </div>

      <div className="md:hidden space-y-2">
        <div className="flex items-center gap-2 px-2"></div>
        <Swiper slidesPerView="auto" spaceBetween={14} className="px-2 pb-4">
          {hasItems &&
            items.map((f) => (
              <SwiperSlide
                key={f.id}
                style={{ width: "70%" }}
                className="flex-shrink-0"
              >
                <FunnelCard funnel={f} onClick={() => onOpen(f)} />
              </SwiperSlide>
            ))}
          <SwiperSlide style={{ width: "70%" }} className="flex-shrink-0">
            <AddFunnelCard onClick={onAdd} label={addLabel} />
          </SwiperSlide>
        </Swiper>
      </div>
    </section>
  );
}

/** =================== Página =================== */
export default function FunnelsGallery({
  toBoardPath = (id) => `/crm?f=${id}`,
}) {
  const navigate = useNavigate();
  const { user } = useApplication();
  const { data: funnels = [], isLoading } = useQuery("funnels", getFunnels);
  const { data: teams = [], isLoading: loadingTeams } = useQuery(
    "teams",
    getTeams
  );

  // ====== estado do TemplateModal (qual visibilidade/time usar ao abrir) ======
  const [templateCfg, setTemplateCfg] = useState({
    open: false,
    visibility: "PUBLIC",
    teamId: null,
    teamName: "",
  });

  // ====== estado do construtor personalizado ======
  const [customBuilder, setCustomBuilder] = useState({
    open: false,
    visibility: "PUBLIC",
    teamId: null,
    teamName: "",
    ownerUserId: null,
  });
  const [showChooser, setShowChooser] = useState(false);

  /** split por visibilidade */
  const { mine, team, pub } = useMemo(() => {
    const list = toArray(funnels);
    const getV = (f) => String(f.visibility || "PRIVATE").toUpperCase();
    const mine = list.filter(
      (f) =>
        getV(f) === "PRIVATE" && (!f.ownerUserId || f.ownerUserId === user?.id)
    );
    const team = list.filter((f) => getV(f) === "TEAM");
    const pub = list.filter((f) => getV(f) === "PUBLIC");
    return { mine, team, pub };
  }, [funnels, user?.id]);

  /** agrupa TEAM por time */
  const teamByGroup = useMemo(() => {
    const groups = new Map();
    team.forEach((f) => {
      const name = String(resolveTeamName(f) || "Sem time");
      if (!groups.has(name)) groups.set(name, []);
      groups.get(name).push(f);
    });
    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([teamName, items]) => {
        // tentativa de obter teamId real (se vier no objeto)
        const any = items.find((x) => x?.team?.id || x?.teamId);
        const teamId = any?.team?.id ?? any?.teamId ?? null;
        return {
          teamName,
          teamId,
          items,
          accent: pickSolid(teamName).bg,
        };
      });
  }, [team]);

  const teamOptions = useMemo(() => {
    const options = [];
    const seenKeys = new Set();
    const addOption = (teamId, teamName) => {
      const label = teamName || "Time";
      const key = teamId ?? label;
      if (seenKeys.has(String(key))) return;
      seenKeys.add(String(key));
      options.push({
        teamName: label,
        teamId: teamId ?? null,
      });
    };

    toArray(teams).forEach((team) => {
      const resolvedId = team?.teamId ?? team?.id ?? team?.team?.id ?? null;
      const resolvedName =
        team?.teamName ??
        team?.name ??
        team?.team?.name ??
        team?.groupName ??
        "Time";
      addOption(resolvedId, resolvedName);
    });
    (teamByGroup || []).forEach(({ teamId, teamName }) =>
      addOption(teamId, teamName)
    );

    return options;
  }, [teams, teamByGroup]);

  const choosePrivate = () => {
    setShowChooser(false);
    setTemplateCfg({
      open: true,
      visibility: "PRIVATE",
      teamId: null,
      teamName: "",
    });
  };

  const choosePublic = () => {
    setShowChooser(false);
    setTemplateCfg({
      open: true,
      visibility: "PUBLIC",
      teamId: null,
      teamName: "",
    });
  };

  const chooseTeam = (teamId, teamName) => {
    setShowChooser(false);
    setTemplateCfg({
      open: true,
      visibility: "TEAM",
      teamId: teamId || null,
      teamName: teamName || "",
    });
  };

  // navegação para o board
  const openBoard = (f) => navigate(toBoardPath(f.id));

  // topo: botão "Novo Quadro" -> usa padrão PUBLIC
  const goNewTop = () => setShowChooser(true);

  // seção privados: abrir modal já com PRIVATE
  const goNewPrivate = () =>
    setTemplateCfg({
      open: true,
      visibility: "PRIVATE",
      teamId: null,
      teamName: "",
    });

  // seção público: abrir modal PUBLIC
  const goNewPublic = () =>
    setTemplateCfg({
      open: true,
      visibility: "PUBLIC",
      teamId: null,
      teamName: "",
    });

  // seção time: abrir modal TEAM com id/nome do grupo (quando houver)
  const goNewTeam = (teamId, teamName) =>
    setTemplateCfg({
      open: true,
      visibility: "TEAM",
      teamId: teamId || null,
      teamName: teamName || "",
    });

  // quando usuário escolhe "Criar do zero" dentro do TemplateModal
  const openCustomBuilder = ({
    defaultVisibility,
    defaultTeamId,
    defaultTeamName,
    ownerUserId,
  }) => {
    setTemplateCfg((p) => ({ ...p, open: false }));
    setCustomBuilder({
      open: true,
      visibility: defaultVisibility || "PUBLIC",
      teamId: defaultTeamId || null,
      teamName: defaultTeamName || "",
      ownerUserId:
        ownerUserId || (defaultVisibility === "PRIVATE" ? user?.id : null),
    });
  };

  // criar via POST e navegar
  const createAndOpen = async (payload) => {
    const { data } = await axios.post(CRM.FUNNELS, payload);
    const id = data?.data?.id || data?.id;
    if (id) navigate(`/crm?f=${id}`);
  };

  return (
    <div className=" space-y-6">
      <GalleryHeader
        title="Quadros do FVR"
        subtitle="Organize seus quadros por time, público e privado"
        count={
          (toArray(mine).length || 0) +
          (toArray(pub).length || 0) +
          (toArray(team).length || 0)
        }
        onCreate={goNewTop}
        onSearch={() => {}}
      />

      {isLoading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="h-32 rounded-2xl border border-gray-200 bg-white dark:bg-white/5 shadow-sm animate-pulse"
            />
          ))}
        </div>
      ) : (
        <div className="space-y-7">
          {/* Privados */}
          <Section
            title={`Quadros de ${user?.name || "Eu"}`}
            accent="#0F172A"
            items={mine}
            onOpen={openBoard}
            onAdd={goNewPrivate}
            addLabel="Novo quadro privado"
          />

          {/* Times — uma seção por time */}
          {teamByGroup.length > 0 &&
            teamByGroup.map(({ teamName, teamId, items, accent }) => (
              <Section
                key={teamName}
                title={`Time — ${teamName}`}
                accent={accent}
                items={items}
                onOpen={openBoard}
                onAdd={() => goNewTeam(teamId, teamName)}
                addLabel="Novo quadro do time"
              />
            ))}

          {/* Públicos */}
          <Section
            title="Quadros públicos"
            subtitle="Visíveis para toda a organização"
            accent="#1D4ED8"
            items={pub}
            onOpen={openBoard}
            onAdd={goNewPublic}
            addLabel="Novo quadro público"
          />
        </div>
      )}

      <ChooseVisibilityModal
        show={showChooser}
        onClose={() => setShowChooser(false)}
        onPickPrivate={choosePrivate}
        onPickPublic={choosePublic}
        teams={teamOptions}
        loadingTeams={loadingTeams}
        onPickTeam={chooseTeam}
      />

      {/* Modal de templates (visibilidade definida pelo contexto da seção) */}
      <FunnelTemplateModal
        show={templateCfg.open}
        onClose={() => setTemplateCfg((p) => ({ ...p, open: false }))}
        defaultVisibility={templateCfg.visibility}
        defaultTeamId={templateCfg.teamId}
        defaultTeamName={templateCfg.teamName}
        ownerUserId={templateCfg.visibility === "PRIVATE" ? user?.id : null}
        onCreateFromTemplate={async (payload) => createAndOpen(payload)}
        onCreateCustom={openCustomBuilder}
      />

      {/* Modal “criar do zero” (custom builder) */}
      <FunnelCustomBuilderModal
        show={customBuilder.open}
        onClose={() => setCustomBuilder((p) => ({ ...p, open: false }))}
        onCreate={async (payload) => createAndOpen(payload)}
        defaultVisibility={customBuilder.visibility}
        defaultTeamId={customBuilder.teamId}
        defaultTeamName={customBuilder.teamName}
        ownerUserId={customBuilder.ownerUserId}
      />
    </div>
  );
}
